import {
  ArrowRightStartOnRectangleIcon,
  KeyIcon,
} from "@heroicons/react/16/solid";
import { ChevronDownIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../components/auth/AuthenticationContext";
import { ChangePasswordModal } from "./change-password-modal";

export const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const { signOut, account } = useAuth();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAccountMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node)
      ) {
        setIsAccountMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  const openChangePasswordModal = () => {
    setIsAccountMenuOpen(false);
    setIsChangePasswordModalOpen(true);
  };

  const handleSignOut = () => {
    setIsAccountMenuOpen(false);
    signOut();
  };

  return (
    <>
      <nav className="border-b bg-white">
        <div className="flex items-center justify-between gap-3 px-4 py-3 md:px-6 md:py-4">
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="Physio Analytics Logo"
              className="h-8 w-auto flex-shrink-0 md:h-10"
            />
            {account?.role === "admin" && (
              <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-white">
                {t("navbar.admin")}
              </span>
            )}
          </div>

          <div className="relative" ref={accountMenuRef}>
            <button
              type="button"
              title={t("navbar.accountMenu")}
              aria-label={t("navbar.accountMenu")}
              aria-haspopup="menu"
              aria-expanded={isAccountMenuOpen}
              onClick={() => setIsAccountMenuOpen((isOpen) => !isOpen)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-primary transition-colors hover:bg-indigo-50 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <UserCircleIcon className="h-7 w-7" />
              <ChevronDownIcon className="absolute bottom-1 right-0 h-3.5 w-3.5 rounded-full bg-white" />
            </button>

            {isAccountMenuOpen && (
              <div
                role="menu"
                aria-label={t("navbar.accountMenu")}
                className="absolute right-0 top-12 z-40 min-w-56 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={openChangePasswordModal}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-800 transition-colors hover:bg-indigo-50 hover:text-indigo-800 focus:bg-indigo-50 focus:outline-none"
                >
                  <KeyIcon className="h-5 w-5 text-primary" />
                  {t("navbar.changePassword")}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-800 transition-colors hover:bg-indigo-50 hover:text-indigo-800 focus:bg-indigo-50 focus:outline-none"
                >
                  <ArrowRightStartOnRectangleIcon className="h-5 w-5 text-primary" />
                  {t("navbar.signOut")}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
      <ChangePasswordModal
        open={isChangePasswordModalOpen}
        handleClose={() => setIsChangePasswordModalOpen(false)}
      />
    </>
  );
};
